import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Badge,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Input,
  SegmentedControl,
} from '@mlm/ui';
import { AR_PERMS } from '@mlm/shared';
import { listCreditMemos, type CreditMemoRow, type CreditMemoStatus } from '../../lib/api';
import { useHasPerm } from '../../lib/auth-store';
import { PageHeader } from './_shared';
import { CreditMemoEditorDialog } from './ar-credit-memo-editor';

/**
 * Credit Memos (R2 Phase E). Credit given to a customer — returned goods, a
 * price adjustment, a billing error — which then settles their billings or is
 * refunded. "Credit Memo" is the document's name; like every AR paper here it
 * is not a BIR-registered document.
 */

const TABS = [
  { value: 'all', label: 'All' },
  { value: 'draft', label: 'Draft' },
  { value: 'open', label: 'Open' },
  { value: 'applied', label: 'Applied' },
  { value: 'refunded', label: 'Refunded' },
  { value: 'void', label: 'Void' },
] as const;

function peso(v: string | number | null | undefined): string {
  const n = Number(v ?? 0);
  return `₱${(Number.isFinite(n) ? n : 0).toLocaleString('en-PH', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}

function shortDate(iso: string | null | undefined): string {
  if (!iso) return '—';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleDateString();
}

function StatusBadge({ row }: { row: CreditMemoRow }) {
  if (row.status === 'void') return <Badge variant="muted">Void</Badge>;
  if (row.status === 'draft') return <Badge variant="warning">Draft</Badge>;
  if (row.status === 'applied') return <Badge variant="success">Applied</Badge>;
  if (row.status === 'refunded') return <Badge variant="success">Refunded</Badge>;
  const partly = Number(row.amountUnapplied) < Number(row.total);
  return <Badge variant="secondary">{partly ? 'Partly used' : 'Open'}</Badge>;
}

export function CreditMemosPage() {
  const canWrite = useHasPerm(AR_PERMS.creditMemoWrite);
  const [tab, setTab] = useState<string>('all');
  const [q, setQ] = useState('');
  const [editing, setEditing] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);

  const listQ = useQuery({
    queryKey: ['credit-memos', tab, q],
    queryFn: () =>
      listCreditMemos({
        status: tab === 'all' ? undefined : (tab as CreditMemoStatus),
        q: q.trim() || undefined,
      }),
  });
  const rows = listQ.data ?? [];

  return (
    <div className="space-y-4">
      <PageHeader
        title="Credit Memos"
        subtitle="Credit a customer for returned goods, a price adjustment or a billing error. Posting puts the credit on their account; it then settles their billings or is refunded."
        actions={
          canWrite ? <Button onClick={() => setCreating(true)}>New credit memo</Button> : undefined
        }
      />

      <Card>
        <CardHeader>
          <CardTitle>Filters</CardTitle>
        </CardHeader>
        <CardContent className="flex flex-wrap items-end gap-4">
          <SegmentedControl
            value={tab}
            onChange={(v) => setTab(String(v))}
            options={TABS.map((t) => ({ value: t.value as string, label: t.label as string }))}
          />
          <div className="w-64 space-y-1">
            <label className="text-xs text-muted-foreground" htmlFor="cm-search">
              Search
            </label>
            <Input
              id="cm-search"
              className="h-9"
              placeholder="Doc number, reference or customer"
              value={q}
              onChange={(e) => setQ(e.target.value)}
            />
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-0">
          {listQ.isLoading ? (
            <p className="p-4 text-sm text-muted-foreground">Loading…</p>
          ) : rows.length === 0 ? (
            <p className="p-8 text-center text-sm text-muted-foreground">
              No credit memos here yet.
            </p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="border-b text-left text-xs uppercase tracking-wide text-muted-foreground">
                  <tr>
                    <th className="px-4 py-2">Doc no</th>
                    <th className="px-4 py-2">Customer</th>
                    <th className="px-4 py-2">Date</th>
                    <th className="px-4 py-2">Reason</th>
                    <th className="px-4 py-2">Against</th>
                    <th className="px-4 py-2">Status</th>
                    <th className="px-4 py-2 text-right">Total</th>
                    <th className="px-4 py-2 text-right">Available</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r) => (
                    <tr
                      key={r.id}
                      className="cursor-pointer border-b last:border-0 hover:bg-muted/30"
                      onClick={() => setEditing(r.id)}
                    >
                      <td className="px-4 py-2 font-mono text-xs">{r.docNo}</td>
                      <td className="px-4 py-2">{r.customerName}</td>
                      <td className="px-4 py-2">{shortDate(r.memoDate)}</td>
                      <td className="px-4 py-2">{r.reason}</td>
                      <td className="px-4 py-2 font-mono text-xs">{r.againstDocNo ?? '—'}</td>
                      <td className="px-4 py-2">
                        <StatusBadge row={r} />
                      </td>
                      <td className="px-4 py-2 text-right font-mono tabular-nums">
                        {peso(r.total)}
                      </td>
                      <td className="px-4 py-2 text-right font-mono tabular-nums">
                        {r.status === 'void' || r.status === 'draft'
                          ? '—'
                          : peso(r.amountUnapplied)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {creating ? (
        <CreditMemoEditorDialog creditMemoId={null} onClose={() => setCreating(false)} />
      ) : null}
      {editing ? (
        <CreditMemoEditorDialog creditMemoId={editing} onClose={() => setEditing(null)} />
      ) : null}
    </div>
  );
}
